import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TimeInput } from '../Common/TimeInput';
import { Input, InputCheck } from '../Common/Input';
import { AccessibleSection } from '../Common/ValiditySection';
import ValiditySection from '../Common/ValiditySection';
import OpeningHoursEditor from './OpeningHoursEditor';
import OpeningHoursSelector from './OpeningHoursSelector';
import Tag from '../Common/Tag';
import { Advice } from '../Common/Card';
import Tabs from '../Common/Tabs';
import { FormLabel, FormSelector, IconHeader, ValidationError } from '../Common/Dialog';

import { Date as DateIcon, Plus, Delete, Clock, Reccuring, OneTime, Multiday, FreeAccess } from '../Icons/CustomIcons';

const EVENT_TYPES = {
  REGULAR_HOURS: 'regular_hours',
  RECURRING_EVENT: 'recurring_event',
  SINGLE_EVENT: 'single_event',
  MULTI_DAY: 'multi_day',
  FREELY_ACCESSIBLE: 'freely_accessible'
};

const RECURRENCE_PATTERNS = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  BIWEEKLY: 'biweekly',
  TRIWEEKLY: 'triweekly',
  FOURWEEKLY: 'fourweekly',
  MONTHLY_FIRST: 'monthly_first',
  MONTHLY_SECOND: 'monthly_second',
  MONTHLY_THIRD: 'monthly_third',
  MONTHLY_FOURTH: 'monthly_fourth',
  MONTHLY_LAST: 'monthly_last'
};

const RECURRENCE_TYPES = {
  DAILY: 'daily_type',
  WEEKLY: 'weekly_type',
  MONTHLY: 'monthly_type'
};

const DAYS_DE = {
  monday: 'Montag',
  tuesday: 'Dienstag',
  wednesday: 'Mittwoch',
  thursday: 'Donnerstag',
  friday: 'Freitag',
  saturday: 'Samstag',
  sunday: 'Sonntag'
};

const StartEndTime = ({ startTime, endTime, onChangeStart, onChangeEnd, suffix = '' }) => (
  <div className="start-end-time">
    <div className="start-end-time--inner">
      <label className="body Font-M-I-130-T Colors-Black start-end-time--label">Startzeit{suffix}</label>
      <TimeInput
        value={startTime}
        onChange={onChangeStart}
        className="input text__input"
      />
    </div>
    <div className="start-end-time--inner">
      <label className="body Font-M-I-130-T Colors-Black start-end-time--label">Endzeit{suffix} (optional)</label>
      <TimeInput
        value={endTime}
        onChange={onChangeEnd}
        className="input text__input"
      />
    </div>
  </div>
);

const AddTimeButton = ({ onClick, date = false }) => (
  <button
    type="button"
    onClick={onClick}
    className="Font-M-I-130-T btn-add-time-full"
  >
    <Plus size={20} />
    {date ? 'Datum hinzufügen' : 'Zeit hinzufügen'}
  </button>
);

const RemoveTimeButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="Font-M-I-130-T btn-remove-time"
  >
    <Delete size={18} />
    Löschen
  </button>
);

/* Component for managing event schedules in the ActivityForm.
  Handles different event types (regular hours, recurring events, single/multi-day events, freely accessible)
  and their specific input fields and validation logic. Exposes a validate() function to the parent form via a ref,
  which checks the inputs and returns an error message if validation fails, or null if it succeeds.
  On successful validation, it also saves the schedule data to state and notifies the parent about the save status.
 */
const EventDatesBlock = ({ description, message, dates, onAdd, onRemove, onChange }) => (
  <div className="schedule-content">
    <div className="modal-content__inner">
      {message && (
        <div className={`message message-${message.type} mb-4`}>
          {message.text}
        </div>
      )}
      <p className="body-m body--semibold">{description}</p>
      {dates.map((eventDate, index) => (
        <div key={index} className="single-event-date-group">
          <div className="single-event-date-header">
            <h3 className="icon-header">
              <span className="icon-wrap"><DateIcon size={20} color="#fff" /></span>
              {dates.length > 1 ? `Termin ${index + 1}` : 'Datum'}
            </h3>
            <div className="single-event-date-actions">
              {dates.length > 1 && (
                <RemoveTimeButton onClick={() => onRemove(index)} />
              )}
            </div>
          </div>
          <div className="modal-content__inner">
            <div>
              <Input
                type="date"
                value={eventDate.date || ''}
                onChange={(e) => onChange(index, 'date', e.target.value)}
                className="text__input"
                required
              />
            </div>
            <StartEndTime
              startTime={eventDate.start_time || '09:00'}
              endTime={eventDate.end_time || ''}
              onChangeStart={(e) => onChange(index, 'start_time', e.target.value)}
              onChangeEnd={(e) => onChange(index, 'end_time', e.target.value)}
            />
          </div>
        </div>
      ))}
      <AddTimeButton onClick={onAdd} date />
    </div>
  </div>
);

const EventScheduleManager = ({ 
  eventType, 
  openingHours, 
  eventSchedule, 
  isSeasonal,
  validFrom = '',
  validUntil = '',
  onValidityChange,
  isEditMode = false,
  onEventTypeChange, 
  onOpeningHoursChange,
  onEventScheduleChange,
  onSeasonalChange,
  onImportOpeningHours,
  onImportFromWebsite,
  address,
  website,
  onWebsiteChange,
  canImportFromGoogle = false,
  initialOpeningHoursMode = '',
  onOpeningHoursModeChange,
  onSaveStatusChange, // New prop to notify parent of save status
  validateRef         // Ref to expose validate() function to parent
}) => {
  const [localSchedule, setLocalSchedule] = useState(eventSchedule || {});
  const [recurrenceType, setRecurrenceType] = useState(() => {
    if (!eventSchedule?.recurrence) return RECURRENCE_TYPES.WEEKLY;
    const r = eventSchedule.recurrence;
    if (r === RECURRENCE_PATTERNS.DAILY) return RECURRENCE_TYPES.DAILY;
    if ([RECURRENCE_PATTERNS.WEEKLY, RECURRENCE_PATTERNS.BIWEEKLY, RECURRENCE_PATTERNS.TRIWEEKLY, RECURRENCE_PATTERNS.FOURWEEKLY].includes(r)) return RECURRENCE_TYPES.WEEKLY;
    if (r.startsWith('monthly_')) return RECURRENCE_TYPES.MONTHLY;
    return RECURRENCE_TYPES.WEEKLY;
  });
  const [dailySchedule, setDailySchedule] = useState(() => {
    if (eventSchedule?.recurrence === RECURRENCE_PATTERNS.DAILY) {
      return {
        start_time: eventSchedule.start_time || '',
        end_time: eventSchedule.end_time || '',
        recurrence: eventSchedule.recurrence,
        additional_times: eventSchedule.additional_times || []
      };
    }
    return { start_time: '', end_time: '', recurrence: RECURRENCE_PATTERNS.DAILY, additional_times: [] };
  });
  const [weeklySchedule, setWeeklySchedule] = useState(() => {
    if (eventSchedule?.recurrence && [RECURRENCE_PATTERNS.WEEKLY, RECURRENCE_PATTERNS.BIWEEKLY, RECURRENCE_PATTERNS.TRIWEEKLY, RECURRENCE_PATTERNS.FOURWEEKLY].includes(eventSchedule.recurrence)) {
      return {
        day_of_week: eventSchedule.day_of_week || '',
        start_time: eventSchedule.start_time || '',
        end_time: eventSchedule.end_time || '',
        recurrence: eventSchedule.recurrence,
        additional_times: eventSchedule.additional_times || []
      };
    }
    return { day_of_week: '', start_time: '', end_time: '', recurrence: RECURRENCE_PATTERNS.WEEKLY, additional_times: [] };
  });
  const [monthlySchedule, setMonthlySchedule] = useState(() => {
    if (eventSchedule?.recurrence?.startsWith('monthly_')) {
      return {
        day_of_week: eventSchedule.day_of_week || '',
        start_time: eventSchedule.start_time || '',
        end_time: eventSchedule.end_time || '',
        recurrence: eventSchedule.recurrence,
        additional_times: eventSchedule.additional_times || []
      };
    }
    return { day_of_week: '', start_time: '', end_time: '', recurrence: RECURRENCE_PATTERNS.MONTHLY_FIRST, additional_times: [] };
  });
  const [message] = useState(null);
  const [singleEventDates, setSingleEventDates] = useState(() => {
    if (eventSchedule?.dates && Array.isArray(eventSchedule.dates)) return eventSchedule.dates;
    if (eventSchedule?.date) {
      return [{ date: eventSchedule.date, start_time: eventSchedule.start_time || '09:00', end_time: eventSchedule.end_time || '' }];
    }
    return [{ date: '', start_time: '09:00', end_time: '' }];
  });
  const [timeDataSaved, setTimeDataSaved] = useState(() => {
    const hasData = eventSchedule && Object.keys(eventSchedule).length > 0;
    const hasOpeningHours = openingHours && openingHours.length > 0;
    return hasData || hasOpeningHours;
  });
  const [localIsSeasonal, setLocalIsSeasonal] = useState(isSeasonal || false);
  const [localValidFrom, setLocalValidFrom] = useState(validFrom);
  const [localValidUntil, setLocalValidUntil] = useState(validUntil);
  // eslint-disable-next-line no-unused-vars
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Ref to skip resetting validated states on initial mount when event type is set from props
  const prevEventType = useRef(eventType);

  // Drag-to-scroll for event-type-options
  const scrollContainerRef = useRef(null);
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;
    const onMouseDown = (e) => {
      isDown = true;
      el.classList.add('dragging');
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    };
    const onMouseLeave = () => { isDown = false; el.classList.remove('dragging'); };
    const onMouseUp = () => { isDown = false; el.classList.remove('dragging'); };
    const onMouseMove = (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      el.scrollLeft = scrollLeft - (x - startX);
    };
    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('mouseleave', onMouseLeave);
    el.addEventListener('mouseup', onMouseUp);
    el.addEventListener('mousemove', onMouseMove);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('mouseleave', onMouseLeave);
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('mousemove', onMouseMove);
    };
  }, []);
  // Im Edit-Modus: Setze validated States direkt bei der Initialisierung
  const [, setDailyValidated] = useState(
    isEditMode && eventType === EVENT_TYPES.RECURRING_EVENT &&
    eventSchedule?.recurrence === RECURRENCE_PATTERNS.DAILY
  );
  const [, setWeeklyValidated] = useState(
    isEditMode && eventType === EVENT_TYPES.RECURRING_EVENT &&
    [RECURRENCE_PATTERNS.WEEKLY, RECURRENCE_PATTERNS.BIWEEKLY, RECURRENCE_PATTERNS.TRIWEEKLY, RECURRENCE_PATTERNS.FOURWEEKLY].includes(eventSchedule?.recurrence)
  );
  const [, setMonthlyValidated] = useState(
    isEditMode && eventType === EVENT_TYPES.RECURRING_EVENT &&
    !!eventSchedule?.recurrence?.startsWith('monthly_')
  );
  const [, setSingleEventValidated] = useState(
    isEditMode && (eventType === EVENT_TYPES.SINGLE_EVENT || eventType === EVENT_TYPES.MULTI_DAY)
  );
  const [, setFreelyAccessibleValidated] = useState(
    isEditMode && eventType === EVENT_TYPES.FREELY_ACCESSIBLE
  );
  const [openingHoursMode, setOpeningHoursMode] = useState(
    () => initialOpeningHoursMode || ((openingHours && openingHours.length > 0) ? 'manual' : 'auto')
  );
  const [importedFromWebsite, setImportedFromWebsite] = useState(false);
  const [websiteStatus, setWebsiteStatus] = useState('idle'); // 'idle' | 'loading' | 'error' | 'success'
  const [websiteErrorMsg, setWebsiteErrorMsg] = useState('');
  const openingHoursEditorRef = useRef(null);

  useEffect(() => {
    // Sync openingHoursMode: manual when opening hours data exist, auto otherwise
    if (openingHours && openingHours.length > 0) {
      setOpeningHoursMode('manual');
    } else if (eventType === EVENT_TYPES.REGULAR_HOURS && !initialOpeningHoursMode) {
      setOpeningHoursMode('auto');
    }

    setLocalSchedule(eventSchedule || {});
    
    // Initialize single event dates from eventSchedule
    if (eventSchedule?.dates && Array.isArray(eventSchedule.dates)) {
      setSingleEventDates(eventSchedule.dates);
    } else if (eventSchedule?.date) {
      // Legacy single date support
      setSingleEventDates([{
        date: eventSchedule.date,
        start_time: eventSchedule.start_time || '09:00',
        end_time: eventSchedule.end_time || ''
      }]);
    } else if (!eventSchedule || Object.keys(eventSchedule).length === 0) {
      // Reset to default when eventSchedule is empty
      setSingleEventDates([{ date: '', start_time: '09:00', end_time: '' }]);
    }
    
    // Mark as saved when data is loaded
    const hasData = eventSchedule && Object.keys(eventSchedule).length > 0;
    const hasOpeningHours = openingHours && openingHours.length > 0;
    
    if (hasData || hasOpeningHours) {
      setTimeDataSaved(true);
      setHasUnsavedChanges(false);
    } else {
      setTimeDataSaved(false);
    }
    
    // Determine which type of recurrence is set
    if (eventSchedule?.recurrence) {
      const recurrence = eventSchedule.recurrence;
      if (recurrence === RECURRENCE_PATTERNS.DAILY) {
        setRecurrenceType(RECURRENCE_TYPES.DAILY);
        setDailySchedule({
          start_time: eventSchedule.start_time || '',
          end_time: eventSchedule.end_time || '',
          recurrence: eventSchedule.recurrence,
          additional_times: eventSchedule.additional_times || []
        });
      } else if ([RECURRENCE_PATTERNS.WEEKLY, RECURRENCE_PATTERNS.BIWEEKLY, RECURRENCE_PATTERNS.TRIWEEKLY, RECURRENCE_PATTERNS.FOURWEEKLY].includes(recurrence)) {
        setRecurrenceType(RECURRENCE_TYPES.WEEKLY);
        setWeeklySchedule({
          day_of_week: eventSchedule.day_of_week || '',
          start_time: eventSchedule.start_time || '',
          end_time: eventSchedule.end_time || '',
          recurrence: eventSchedule.recurrence,
          additional_times: eventSchedule.additional_times || []
        });
      } else if (recurrence.startsWith('monthly_')) {
        setRecurrenceType(RECURRENCE_TYPES.MONTHLY);
        setMonthlySchedule({
          day_of_week: eventSchedule.day_of_week || '',
          start_time: eventSchedule.start_time || '',
          end_time: eventSchedule.end_time || '',
          recurrence: eventSchedule.recurrence,
          additional_times: eventSchedule.additional_times || []
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSchedule, openingHours]);

  // Reset saved status when event type changes (but not on initial mount)
  useEffect(() => {
    if (prevEventType.current === eventType) {
      return; // Skip: eventType has not actually changed (e.g. StrictMode double-invoke)
    }
    prevEventType.current = eventType;
    // Only reset when user actively changes event type
    setTimeDataSaved(false);
    setHasUnsavedChanges(false);
    setDailyValidated(false);
    setWeeklyValidated(false);
    setMonthlyValidated(false);
    setSingleEventValidated(false);
    setFreelyAccessibleValidated(false);
  }, [eventType]);

  // Notify parent about save status changes
  useEffect(() => {
    if (onSaveStatusChange) {
      onSaveStatusChange(timeDataSaved);
    }
  }, [timeDataSaved, onSaveStatusChange]);

  // Clear parent step3Error when recurring schedule sub-states change (user is editing)
  const isRecurringMountRef = useRef(true);
  useEffect(() => {
    if (isRecurringMountRef.current) {
      isRecurringMountRef.current = false;
      return;
    }
    if (eventType === EVENT_TYPES.RECURRING_EVENT) {
      onEventScheduleChange(localSchedule);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailySchedule, weeklySchedule, monthlySchedule, recurrenceType]);

  const handleScheduleChange = (updates) => {
    const newSchedule = { ...localSchedule, ...updates };
    setLocalSchedule(newSchedule);
    onEventScheduleChange(newSchedule);
    setHasUnsavedChanges(true);
  };

  const handleAddSingleEventDate = () => {
    const newDates = [...singleEventDates, { date: '', start_time: '09:00', end_time: '' }];
    setSingleEventDates(newDates);
    handleScheduleChange({ dates: newDates });
    setSingleEventValidated(false);
  };

  const handleRemoveSingleEventDate = (index) => {
    if (singleEventDates.length > 1) {
      const newDates = singleEventDates.filter((_, i) => i !== index);
      setSingleEventDates(newDates);
      handleScheduleChange({ dates: newDates });
      setSingleEventValidated(false);
    }
  };

  const handleSingleEventDateChange = (index, field, value) => {
    const newDates = [...singleEventDates];
    newDates[index][field] = value;
    setSingleEventDates(newDates);
    handleScheduleChange({ dates: newDates });
    setSingleEventValidated(false);
  };

  const getMonthlyRecurrenceLabel = (pattern) => {
    const weekdayName = monthlySchedule.day_of_week ? DAYS_DE[monthlySchedule.day_of_week] : '[Wochentag]';
    const labels = {
      [RECURRENCE_PATTERNS.MONTHLY_FIRST]: `Jeden ersten ${weekdayName} im Monat`,
      [RECURRENCE_PATTERNS.MONTHLY_SECOND]: `Jeden zweiten ${weekdayName} im Monat`,
      [RECURRENCE_PATTERNS.MONTHLY_THIRD]: `Jeden dritten ${weekdayName} im Monat`,
      [RECURRENCE_PATTERNS.MONTHLY_FOURTH]: `Jeden vierten ${weekdayName} im Monat`,
      [RECURRENCE_PATTERNS.MONTHLY_LAST]: `Jeden letzten ${weekdayName} im Monat`
    };
    return labels[pattern] || pattern;
  };

  const handleOpeningHoursSave = async (hours) => {
    onOpeningHoursChange(hours);
    setTimeDataSaved(true);
    setHasUnsavedChanges(false);
    return Promise.resolve();
  };

  // Validation function exposed to parent via validateRef.
  // Returns null on success, or an error string on failure.
  // On success also saves the data to state/parent.
  const runValidation = useCallback(async () => {
    if (eventType === EVENT_TYPES.REGULAR_HOURS) {
      if (openingHoursMode === 'manual') {
        const err = await openingHoursEditorRef.current?.();
        if (!err && onSaveStatusChange) onSaveStatusChange(true);
        return err ?? null;
      }
      // auto mode: user must have imported opening hours from website
      if (!timeDataSaved) {
        return 'Bitte importieren Sie zuerst die Öffnungszeiten über die Webseite der Location.';
      }
      return null;
    }

    if (eventType === EVENT_TYPES.FREELY_ACCESSIBLE) {
      if (localSchedule.accessible_from && localSchedule.accessible_until &&
          localSchedule.accessible_from >= localSchedule.accessible_until) {
        return '"Zugänglich ab" muss vor "Zugänglich bis" liegen!';
      }
      setTimeDataSaved(true);
      if (onSaveStatusChange) onSaveStatusChange(true);
      setFreelyAccessibleValidated(true);
      setHasUnsavedChanges(false);
      return null;
    }

    if (eventType === EVENT_TYPES.SINGLE_EVENT || eventType === EVENT_TYPES.MULTI_DAY) {
      if (!singleEventDates.some(d => d.date && d.date.trim() !== '')) {
        return 'Bitte geben Sie mindestens ein Datum an.';
      }
      for (let i = 0; i < singleEventDates.length; i++) {
        const ev = singleEventDates[i];
        if (!ev.date || ev.date.trim() === '') {
          return `Termin ${i + 1}: Bitte geben Sie ein Datum an.`;
        }
        if (ev.start_time && ev.end_time && ev.start_time >= ev.end_time) {
          return `Termin ${i + 1}: Die Startzeit muss vor der Endzeit liegen!`;
        }
      }
      const scheduleToSave = { dates: singleEventDates };
      setLocalSchedule(scheduleToSave);
      onEventScheduleChange(scheduleToSave);
      setTimeDataSaved(true);
      if (onSaveStatusChange) onSaveStatusChange(true);
      setSingleEventValidated(true);
      setHasUnsavedChanges(false);
      return null;
    }

    if (eventType === EVENT_TYPES.RECURRING_EVENT) {
      if (recurrenceType === RECURRENCE_TYPES.DAILY) {
        if (!dailySchedule.start_time) return 'Bitte geben Sie eine Startzeit an.';
        if (dailySchedule.start_time && dailySchedule.end_time && dailySchedule.start_time >= dailySchedule.end_time)
          return 'Die Startzeit muss vor der Endzeit liegen!';
        if (localSchedule.valid_from && localSchedule.valid_until && localSchedule.valid_from >= localSchedule.valid_until)
          return '"Gültig ab" muss vor "Gültig bis" liegen!';
        for (let i = 0; i < (dailySchedule.additional_times || []).length; i++) {
          const tp = dailySchedule.additional_times[i];
          if (tp.start_time && tp.end_time && tp.start_time >= tp.end_time)
            return `Uhrzeit ${i + 2}: Die Startzeit muss vor der Endzeit liegen!`;
        }
        const scheduleToSave = {
          start_time: dailySchedule.start_time,
          end_time: dailySchedule.end_time,
          recurrence: dailySchedule.recurrence,
          additional_times: (dailySchedule.additional_times || []).filter(tp => tp.start_time),
          valid_from: localSchedule.valid_from || '',
          valid_until: localSchedule.valid_until || '',
        };
        setLocalSchedule(scheduleToSave);
        onEventScheduleChange(scheduleToSave);
        setTimeDataSaved(true);
        if (onSaveStatusChange) onSaveStatusChange(true);
        setDailyValidated(true);
        setHasUnsavedChanges(false);
        return null;
      }

      if (recurrenceType === RECURRENCE_TYPES.WEEKLY) {
        if (!weeklySchedule.day_of_week || !weeklySchedule.start_time)
          return 'Bitte Wochentag und Startzeit angeben.';
        if (['biweekly', 'triweekly', 'fourweekly'].includes(weeklySchedule.recurrence) && !localSchedule.valid_from)
          return 'Bei mehrwöchigen Wiederholungen muss "Gültig ab" angegeben werden!';
        if (weeklySchedule.start_time && weeklySchedule.end_time && weeklySchedule.start_time >= weeklySchedule.end_time)
          return 'Die Startzeit muss vor der Endzeit liegen!';
        if (localSchedule.valid_from && localSchedule.valid_until && localSchedule.valid_from >= localSchedule.valid_until)
          return '"Gültig ab" muss vor "Gültig bis" liegen!';
        for (let i = 0; i < (weeklySchedule.additional_times || []).length; i++) {
          const tp = weeklySchedule.additional_times[i];
          if (tp.start_time && tp.end_time && tp.start_time >= tp.end_time)
            return `Uhrzeit ${i + 2}: Die Startzeit muss vor der Endzeit liegen!`;
        }
        const scheduleToSave = {
          day_of_week: weeklySchedule.day_of_week,
          start_time: weeklySchedule.start_time,
          end_time: weeklySchedule.end_time,
          recurrence: weeklySchedule.recurrence,
          additional_times: (weeklySchedule.additional_times || []).filter(tp => tp.start_time),
          valid_from: localSchedule.valid_from || '',
          valid_until: localSchedule.valid_until || '',
        };
        setLocalSchedule(scheduleToSave);
        onEventScheduleChange(scheduleToSave);
        setTimeDataSaved(true);
        if (onSaveStatusChange) onSaveStatusChange(true);
        setWeeklyValidated(true);
        setHasUnsavedChanges(false);
        return null;
      }

      if (recurrenceType === RECURRENCE_TYPES.MONTHLY) {
        if (!monthlySchedule.day_of_week || !monthlySchedule.start_time)
          return 'Bitte Wochentag und Startzeit angeben.';
        if (monthlySchedule.start_time && monthlySchedule.end_time && monthlySchedule.start_time >= monthlySchedule.end_time)
          return 'Die Startzeit muss vor der Endzeit liegen!';
        if (localSchedule.valid_from && localSchedule.valid_until && localSchedule.valid_from >= localSchedule.valid_until)
          return '"Gültig ab" muss vor "Gültig bis" liegen!';
        for (let i = 0; i < (monthlySchedule.additional_times || []).length; i++) {
          const tp = monthlySchedule.additional_times[i];
          if (tp.start_time && tp.end_time && tp.start_time >= tp.end_time)
            return `Uhrzeit ${i + 2}: Die Startzeit muss vor der Endzeit liegen!`;
        }
        const scheduleToSave = {
          day_of_week: monthlySchedule.day_of_week,
          start_time: monthlySchedule.start_time,
          end_time: monthlySchedule.end_time,
          recurrence: monthlySchedule.recurrence,
          additional_times: (monthlySchedule.additional_times || []).filter(tp => tp.start_time),
          valid_from: localSchedule.valid_from || '',
          valid_until: localSchedule.valid_until || '',
        };
        setLocalSchedule(scheduleToSave);
        onEventScheduleChange(scheduleToSave);
        setTimeDataSaved(true);
        if (onSaveStatusChange) onSaveStatusChange(true);
        setMonthlyValidated(true);
        setHasUnsavedChanges(false);
        return null;
      }
    }

    return null; // fallback – all other types considered valid
  }, [
    eventType, recurrenceType, timeDataSaved, openingHoursMode,
    localSchedule, dailySchedule, weeklySchedule, monthlySchedule, singleEventDates,
    onEventScheduleChange, onSaveStatusChange,
  ]);

  // Register runValidation on the ref so ActivityForm can call it
  useEffect(() => {
    if (validateRef) validateRef.current = runValidation;
  }, [validateRef, runValidation]);

  // Scroll the active event-type button into view (centered) on mount and when eventType changes.
  // Uses rAF to wait until the browser has painted the layout (getBoundingClientRect returns 0
  // if called synchronously while the parent drawer is still animating into view).
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const container = scrollContainerRef.current;
      if (!container) return;
      const activeBtn = container.querySelector('.tag--selected');
      if (!activeBtn) return;
      const containerRect = container.getBoundingClientRect();
      const btnRect = activeBtn.getBoundingClientRect();
      const offset = (btnRect.left + btnRect.width / 2) - (containerRect.left + containerRect.width / 2);
      container.scrollTo({ left: container.scrollLeft + offset, behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [eventType]);

  const handleValidityChange = (validityData) => {
    // Save validity data in eventSchedule when using regular hours
    const scheduleUpdate = {
      ...localSchedule,
      validFrom: validityData.validFrom || '',
      validUntil: validityData.validUntil || '',
      isSeasonal: validityData.isSeasonal || false
    };
    setLocalSchedule(scheduleUpdate);
    onEventScheduleChange(scheduleUpdate);
  };

  const handleOpeningHoursImport = async () => {
    if (onImportOpeningHours) {
      const result = await onImportOpeningHours();
      return result;
    }
    throw new Error('Import nicht verfügbar');
  };

  return (
    <div className="modal-content">
      <div className="event-type-options" ref={scrollContainerRef}>
        {[
          { type: EVENT_TYPES.REGULAR_HOURS,   label: 'Öffnungszeiten', icon: <Clock size={20} /> },
          { type: EVENT_TYPES.RECURRING_EVENT, label: 'Wiederkehrend',  icon: <Reccuring size={20} /> },
          { type: EVENT_TYPES.SINGLE_EVENT,    label: 'Einzeltermin',   icon: <OneTime size={20} /> },
          { type: EVENT_TYPES.MULTI_DAY,       label: 'Mehrtägig',      icon: <Multiday size={20} /> },
          { type: EVENT_TYPES.FREELY_ACCESSIBLE, label: 'Frei zugänglich', icon: <FreeAccess size={20} /> },
        ].map(({ type, label, icon }) => (
          <Tag
            key={type}
            label={label}
            shape="rectangle"
            iconLeft={icon}
            selected={eventType === type}
            onClick={() => onEventTypeChange(type)}
          />
        ))}
      </div>

      {/* Regular Opening Hours */}
      {eventType === EVENT_TYPES.REGULAR_HOURS && (
        <div className="schedule-content">

          {/* Mode selector */}
          <div className="oh-mode-selector">
            <OpeningHoursSelector initialValue={openingHoursMode} onChange={(mode) => { setOpeningHoursMode(mode); if (onOpeningHoursModeChange) onOpeningHoursModeChange(mode); }} />
          </div>

          {/* Automatisch: Website Field */}
          {openingHoursMode === 'auto' && (
            <div className="modal-content__inner-block">
              <FormLabel htmlFor="body-m">Webseite der Location</FormLabel>
              <InputCheck
                id="website"
                type="url"
                value={website || ''}
                onChange={(e) => {
                  onWebsiteChange && onWebsiteChange(e.target.value);
                  setWebsiteStatus('idle');
                  setWebsiteErrorMsg('');
                }}
                placeholder="https://www.beispiel.ch"
                className="text__input"
                status={websiteStatus}
                onCheckClick={async () => {
                  // 1. Feld leer?
                  if (!website || !website.trim()) {
                    setWebsiteErrorMsg('Keine Webseite angegeben.');
                    return;
                  }
                  // 2. Gültige URL?
                  try {
                    const url = new URL(website.trim());
                    if (!['http:', 'https:'].includes(url.protocol)) throw new Error();
                  } catch {
                    setWebsiteErrorMsg('Keine gültige Website-Adresse.');
                    return;
                  }
                  // 3. Öffnungszeiten abrufen
                  setWebsiteStatus('loading');
                  setWebsiteErrorMsg('');
                  try {
                    const result = await onImportFromWebsite();
                    if (result && result.opening_hours && result.opening_hours.length > 0) {
                      setWebsiteStatus('success');
                      setImportedFromWebsite(true);
                    } else {
                      setWebsiteStatus('error');
                      setWebsiteErrorMsg('Webseite liefert keine Öffnungszeiten.');
                    }
                  } catch {
                    setWebsiteStatus('error');
                    setWebsiteErrorMsg('Webseite liefert keine Öffnungszeiten.');
                  }
                }}
              />

              {websiteErrorMsg && (
                <ValidationError>{websiteErrorMsg}</ValidationError>
              )}
              {!websiteErrorMsg && (
                <Advice text="Die Öffnungszeiten können von der angegebenen Webseite automatisch extrahiert werden." />
              )}
            </div>
          )}

          {/* Manuell: OpeningHoursEditor */}
          {openingHoursMode === 'manual' && (
            <OpeningHoursEditor
              openingHours={openingHours}
              onSave={handleOpeningHoursSave}
              onChange={onOpeningHoursChange}
              onImport={handleOpeningHoursImport}
              canImport={!importedFromWebsite && canImportFromGoogle && !!address}
              validFrom={localSchedule.validFrom || ''}
              validUntil={localSchedule.validUntil || ''}
              isSeasonal={isSeasonal || false}
              isEditMode={isEditMode}
              triggerRef={openingHoursEditorRef}
              onValidityChange={(validityData) => {
                handleValidityChange(validityData);
                if (onSeasonalChange) {
                  onSeasonalChange(validityData.isSeasonal);
                }
              }}
            />
          )}
          
          {/* Validity and Seasonal Section */}
               
          <div className="validity-frame">
            <ValiditySection
              isSeasonal={localIsSeasonal}
              onIsSeasonalChange={setLocalIsSeasonal}
              validFrom={localValidFrom}
              onValidFromChange={(value) => { setLocalValidFrom(value); if (onValidityChange) onValidityChange({ validFrom: value, validUntil: localValidUntil, isSeasonal: localIsSeasonal }); }}
              validUntil={localValidUntil}
              onValidUntilChange={(value) => { setLocalValidUntil(value); if (onValidityChange) onValidityChange({ validFrom: localValidFrom, validUntil: value, isSeasonal: localIsSeasonal }); }}
            />
          </div>
        </div>
      )}


      {/* Recurring Event */}
      {eventType === EVENT_TYPES.RECURRING_EVENT && (
        <div className="schedule-content">

          {message && (
            <div className={`message message-${message.type} mb-4`}>
              {message.text}
            </div>
          )}

          {/* Recurrence Type Selector */}
          <div className="recurrence-type-selector">
            <label className="body-m body--semibold">Wiederkehrenden Terminplan erstellen</label>
            <Tabs
              orientation="horizontal"
              tabs={[
                {
                  label: 'Täglich',
                  onClick: () => setRecurrenceType(RECURRENCE_TYPES.DAILY)
                },
                {
                  label: 'Wöchentlich',
                  onClick: () => setRecurrenceType(RECURRENCE_TYPES.WEEKLY)
                },
                {
                  label: 'Monatlich',
                  onClick: () => setRecurrenceType(RECURRENCE_TYPES.MONTHLY)
                }
              ]}
              activeIndex={
                recurrenceType === RECURRENCE_TYPES.DAILY ? 0 :
                recurrenceType === RECURRENCE_TYPES.WEEKLY ? 1 : 2
              }
            />
          </div>

          {/* Daily Recurrence */}
          {recurrenceType === RECURRENCE_TYPES.DAILY && (
            <div className="recurrence-form">              
              <div className="modal-content__inner">
                <div className="single-event-date-group">
                  <div className="single-event-date-header">    
                    <IconHeader Icon={<Clock size={20} color="#fff" />}>   
                      Uhrzeit
                    </IconHeader>
                    <div className="single-event-date-actions">
                    </div>
                  </div>
                  <StartEndTime
                    startTime={dailySchedule.start_time}
                    endTime={dailySchedule.end_time}
                    onChangeStart={(e) => { setDailySchedule({ ...dailySchedule, start_time: e.target.value }); setDailyValidated(false); }}
                    onChangeEnd={(e) => { setDailySchedule({ ...dailySchedule, end_time: e.target.value }); setDailyValidated(false); }}
                  />
                </div>

                {/* Zusätzliche Uhrzeiten (täglich) */}
                {(dailySchedule.additional_times || []).map((tp, idx) => (
                  <div key={idx} className="single-event-date-group">
                    <div className="single-event-date-header">
                      <IconHeader Icon={<Clock size={20} color="#fff" />}>
                        Uhrzeit
                      </IconHeader>
                      <div className="single-event-date-actions">
                        <RemoveTimeButton
                          onClick={() => {
                            const times = dailySchedule.additional_times.filter((_, i) => i !== idx);
                            setDailySchedule({ ...dailySchedule, additional_times: times });
                            setDailyValidated(false);
                          }}
                        />
                      </div>
                    </div>
                    <StartEndTime
                      startTime={tp.start_time}
                      endTime={tp.end_time}
                      onChangeStart={(e) => { const times = [...dailySchedule.additional_times]; times[idx] = { ...times[idx], start_time: e.target.value }; setDailySchedule({ ...dailySchedule, additional_times: times }); setDailyValidated(false); }}
                      onChangeEnd={(e) => { const times = [...dailySchedule.additional_times]; times[idx] = { ...times[idx], end_time: e.target.value }; setDailySchedule({ ...dailySchedule, additional_times: times }); setDailyValidated(false); }}
                    />
                  </div>
                ))}
                <AddTimeButton
                  onClick={() => {
                    setDailySchedule({
                      ...dailySchedule,
                      additional_times: [...(dailySchedule.additional_times || []), { start_time: '', end_time: '' }]
                    });
                    setDailyValidated(false);
                  }}
                />

                <AccessibleSection
                  isSeasonal={isSeasonal}
                  onSeasonalChange={onSeasonalChange}
                  labelFrom="Gültig ab"
                  labelUntil="Gültig bis (optional)"
                  validFrom={localSchedule.valid_from}
                  validUntil={localSchedule.valid_until}
                  onChangeFrom={(e) => { handleScheduleChange({ valid_from: e.target.value }); setDailyValidated(false); }}
                  onChangeUntil={(e) => { handleScheduleChange({ valid_until: e.target.value }); setDailyValidated(false); }}
                />

              </div>
            </div>
          )}

          {/* Weekly Recurrence */}
          {recurrenceType === RECURRENCE_TYPES.WEEKLY && (
            <div className="recurrence-form">
              <div className="modal-content__inner">
                <div className="form-selector-group">
                  <IconHeader Icon={<Multiday size={20} color="#fff" />}>
                    Wochentag
                  </IconHeader>
                  <FormSelector
                    value={weeklySchedule.day_of_week}
                    onChange={(e) => {
                      setWeeklySchedule({ ...weeklySchedule, day_of_week: e.target.value });
                      setWeeklyValidated(false);
                    }}
                  >
                    <option value="">Bitte wählen...</option>
                    {Object.entries(DAYS_DE).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </FormSelector>
                </div>

                <div className="single-event-date-group">
                  <div className="single-event-date-header">
                    <IconHeader Icon={<Clock size={20} color="#fff" />}>
                      Uhrzeit 1
                    </IconHeader>
                    <div className="single-event-date-actions">
                    </div>
                  </div>
                  <StartEndTime
                    startTime={weeklySchedule.start_time}
                    endTime={weeklySchedule.end_time}
                    onChangeStart={(e) => { setWeeklySchedule({ ...weeklySchedule, start_time: e.target.value }); setWeeklyValidated(false); }}
                    onChangeEnd={(e) => { setWeeklySchedule({ ...weeklySchedule, end_time: e.target.value }); setWeeklyValidated(false); }}
                  />
                </div>

                {/* Zusätzliche Uhrzeiten (wöchentlich) */}
                {(weeklySchedule.additional_times || []).map((tp, idx) => (
                  <div key={idx} className="single-event-date-group">
                    <div className="single-event-date-header">
                      <IconHeader Icon={<Clock size={20} color="#fff" />}>
                        Uhrzeit {idx + 2}
                      </IconHeader>
                      <div className="single-event-date-actions">
                        <RemoveTimeButton
                          onClick={() => {
                            const times = weeklySchedule.additional_times.filter((_, i) => i !== idx);
                            setWeeklySchedule({ ...weeklySchedule, additional_times: times });
                            setWeeklyValidated(false);
                          }}
                        />
                      </div>
                    </div>
                    <StartEndTime
                      startTime={tp.start_time}
                      endTime={tp.end_time}
                      suffix={` ${idx + 2}`}
                      onChangeStart={(e) => { const times = [...weeklySchedule.additional_times]; times[idx] = { ...times[idx], start_time: e.target.value }; setWeeklySchedule({ ...weeklySchedule, additional_times: times }); setWeeklyValidated(false); }}
                      onChangeEnd={(e) => { const times = [...weeklySchedule.additional_times]; times[idx] = { ...times[idx], end_time: e.target.value }; setWeeklySchedule({ ...weeklySchedule, additional_times: times }); setWeeklyValidated(false); }}
                    />
                  </div>
                ))}
                <AddTimeButton
                  onClick={() => {
                    setWeeklySchedule({
                      ...weeklySchedule,
                      additional_times: [...(weeklySchedule.additional_times || []), { start_time: '', end_time: '' }]
                    });
                    setWeeklyValidated(false);
                  }}
                />

                <div className="form-selector-group">
                  <IconHeader Icon={<Reccuring size={20} color="#fff" />}>
                    Wiederholung
                  </IconHeader>
                  <FormSelector
                    value={weeklySchedule.recurrence}
                    onChange={(e) => {
                      setWeeklySchedule({ ...weeklySchedule, recurrence: e.target.value });
                      setWeeklyValidated(false);
                    }}
                  >
                    <option value={RECURRENCE_PATTERNS.WEEKLY}>jede Woche</option>
                    <option value={RECURRENCE_PATTERNS.BIWEEKLY}>jede 2. Woche</option>
                    <option value={RECURRENCE_PATTERNS.TRIWEEKLY}>jede 3. Woche</option>
                    <option value={RECURRENCE_PATTERNS.FOURWEEKLY}>jede 4. Woche</option>
                  </FormSelector>
                  {['biweekly', 'triweekly', 'fourweekly'].includes(weeklySchedule.recurrence) && (
                    <p className="text-xs text-orange-600 mt-1">
                      ⚠️ Bei mehrwöchigen Wiederholungen muss "Gültig ab" angegeben werden.
                    </p>
                  )}
                </div>
                
                <AccessibleSection
                  isSeasonal={isSeasonal}
                  onSeasonalChange={onSeasonalChange}
                  labelFrom="Gültig ab"
                  labelUntil="Gültig bis (optional)"
                  validFrom={localSchedule.valid_from}
                  validUntil={localSchedule.valid_until}
                  onValidFromChange={(e) => { handleScheduleChange({ valid_from: e.target.value }); setDailyValidated(false); }}
                  onValidUntilChange={(e) => { handleScheduleChange({ valid_until: e.target.value }); setDailyValidated(false); }}
                  fromRequired={['biweekly', 'triweekly', 'fourweekly'].includes(weeklySchedule.recurrence)}
                />

              </div>
            </div>
          )}

          {/* Monthly Recurrence */}
          {recurrenceType === RECURRENCE_TYPES.MONTHLY && (
            <div className="recurrence-form">
              <div className="modal-content__inner">
                <div className="form-selector-group">
                  <IconHeader Icon={<Multiday size={20} color="#fff" />}>
                    Wochentag
                  </IconHeader>
                  <FormSelector
                    value={monthlySchedule.day_of_week}
                    onChange={(e) => {
                      setMonthlySchedule({ ...monthlySchedule, day_of_week: e.target.value });
                      setMonthlyValidated(false);
                    }}
                  >
                    <option value="">Bitte wählen...</option>
                    {Object.entries(DAYS_DE).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </FormSelector>
                </div>

                <div className="single-event-date-group">
                  <div className="single-event-date-header">
                    <IconHeader Icon={<Clock size={20} color="#fff" />}>
                      Uhrzeit 1
                    </IconHeader>
                    <div className="single-event-date-actions">
                    </div>
                  </div>
                  <StartEndTime
                    startTime={monthlySchedule.start_time}
                    endTime={monthlySchedule.end_time}
                    onChangeStart={(e) => { setMonthlySchedule({ ...monthlySchedule, start_time: e.target.value }); setMonthlyValidated(false); }}
                    onChangeEnd={(e) => { setMonthlySchedule({ ...monthlySchedule, end_time: e.target.value }); setMonthlyValidated(false); }}
                  />
                </div>

                {/* Zusätzliche Uhrzeiten (monatlich) */}
                {(monthlySchedule.additional_times || []).map((tp, idx) => (
                  <div key={idx} className="single-event-date-group">
                    <div className="single-event-date-header">
                      <IconHeader Icon={<Clock size={20} color="#fff" />}>
                        Uhrzeit {idx + 2}
                      </IconHeader>
                      <div className="single-event-date-actions">
                        <RemoveTimeButton
                          onClick={() => {
                            const times = monthlySchedule.additional_times.filter((_, i) => i !== idx);
                            setMonthlySchedule({ ...monthlySchedule, additional_times: times });
                            setMonthlyValidated(false);
                          }}
                        />
                      </div>
                    </div>
                    <StartEndTime
                      startTime={tp.start_time}
                      endTime={tp.end_time}
                      suffix={` ${idx + 2}`}
                      onChangeStart={(e) => { const times = [...monthlySchedule.additional_times]; times[idx] = { ...times[idx], start_time: e.target.value }; setMonthlySchedule({ ...monthlySchedule, additional_times: times }); setMonthlyValidated(false); }}
                      onChangeEnd={(e) => { const times = [...monthlySchedule.additional_times]; times[idx] = { ...times[idx], end_time: e.target.value }; setMonthlySchedule({ ...monthlySchedule, additional_times: times }); setMonthlyValidated(false); }}
                    />
                  </div>
                ))}
                <AddTimeButton
                  onClick={() => {
                    setMonthlySchedule({
                      ...monthlySchedule,
                      additional_times: [...(monthlySchedule.additional_times || []), { start_time: '', end_time: '' }]
                    });
                    setMonthlyValidated(false);
                  }}
                />

                <div className="form-selector-group">
                  <IconHeader Icon={<Reccuring size={20} color="#fff" />}>
                    Wiederholung
                  </IconHeader>
                  <FormSelector
                    value={monthlySchedule.recurrence}
                    onChange={(e) => setMonthlySchedule({ ...monthlySchedule, recurrence: e.target.value })}
                  >
                    <option value={RECURRENCE_PATTERNS.MONTHLY_FIRST}>
                      {getMonthlyRecurrenceLabel(RECURRENCE_PATTERNS.MONTHLY_FIRST)}
                    </option>
                    <option value={RECURRENCE_PATTERNS.MONTHLY_SECOND}>
                      {getMonthlyRecurrenceLabel(RECURRENCE_PATTERNS.MONTHLY_SECOND)}
                    </option>
                    <option value={RECURRENCE_PATTERNS.MONTHLY_THIRD}>
                      {getMonthlyRecurrenceLabel(RECURRENCE_PATTERNS.MONTHLY_THIRD)}
                    </option>
                    <option value={RECURRENCE_PATTERNS.MONTHLY_FOURTH}>
                      {getMonthlyRecurrenceLabel(RECURRENCE_PATTERNS.MONTHLY_FOURTH)}
                    </option>
                    <option value={RECURRENCE_PATTERNS.MONTHLY_LAST}>
                      {getMonthlyRecurrenceLabel(RECURRENCE_PATTERNS.MONTHLY_LAST)}
                    </option>
                  </FormSelector>
                </div>
                
                <AccessibleSection
                  isSeasonal={isSeasonal}
                  onSeasonalChange={onSeasonalChange}
                  labelFrom="Gültig ab"
                  labelUntil="Gültig bis (optional)"
                  validFrom={localSchedule.valid_from}
                  validUntil={localSchedule.valid_until}
                  onValidFromChange={(e) => { handleScheduleChange({ valid_from: e.target.value }); setDailyValidated(false); }}
                  onValidUntilChange={(e) => { handleScheduleChange({ valid_until: e.target.value }); setDailyValidated(false); }}
                />

              </div>
            </div>
          )}
        </div>
      )}

      {/* Single Event */}
      {eventType === EVENT_TYPES.SINGLE_EVENT && (
        <EventDatesBlock
          description="Geben Sie ein oder mehrere Daten für Ihre Veranstaltung an."
          message={message}
          dates={singleEventDates}
          onAdd={handleAddSingleEventDate}
          onRemove={handleRemoveSingleEventDate}
          onChange={handleSingleEventDateChange}
        />
      )}

      {/* Multi-Day Event */}
      {eventType === EVENT_TYPES.MULTI_DAY && (
        <EventDatesBlock
          description="Geben Sie mehrere Daten für Ihre mehrtägige Veranstaltung an. Der Event erscheint als Activity Card nur für den ersten Termin."
          message={message}
          dates={singleEventDates}
          onAdd={handleAddSingleEventDate}
          onRemove={handleRemoveSingleEventDate}
          onChange={handleSingleEventDateChange}
        />
      )}

      {/* Freely Accessible */}
      {eventType === EVENT_TYPES.FREELY_ACCESSIBLE && (
        <div className="schedule-content">

          {message && (
            <div className={`message message-${message.type} mb-4`}>
              {message.text}
            </div>
          )}
          
          <div className="modal-content__inner">   
            <p className="body-m body--semibold">
              Dieser Event ist im gegebenen Zeitraum frei zugänglich.
            </p>         
            <AccessibleSection
              isSeasonal={isSeasonal}
              onSeasonalChange={onSeasonalChange}
              labelFrom="Zugänglich ab"
              labelUntil="Zugänglich bis (optional)"
              validFrom={localSchedule.accessible_from}
              validUntil={localSchedule.accessible_until}
              onValidFromChange={(e) => { handleScheduleChange({ accessible_from: e.target.value }); setFreelyAccessibleValidated(false); }}
              onValidUntilChange={(e) => { handleScheduleChange({ accessible_until: e.target.value }); setFreelyAccessibleValidated(false); }}
            />
            
            {(localSchedule.accessible_from || localSchedule.accessible_until) && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  Dieser Event ist{' '}
                  {localSchedule.accessible_from && (
                    <>
                      ab dem <strong>{new Date(localSchedule.accessible_from).toLocaleDateString('de-DE')}</strong>
                    </>
                  )}
                  {localSchedule.accessible_from && localSchedule.accessible_until && ' '}
                  {localSchedule.accessible_until && (
                    <>
                      bis zum <strong>{new Date(localSchedule.accessible_until).toLocaleDateString('de-DE')}</strong>
                    </>
                  )}
                  {' '}zugänglich.
                </p>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
};

export default EventScheduleManager;
